import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { isEmpty } from 'remeda'
import type { Wishlist, WishlistItem } from '@velasca/types'
import { getIdFromShopifyGid } from '@velasca/utils/get-id-from-shopify-gid'
import { $settings } from '@velasca/utils/globals'
import type {
  WishlistContext,
  WishlistProductData,
  WishlistProviderProps,
} from './Wishlist.types'

const WISHLIST_NAME = 'default'

const WishlistContext = createContext<WishlistContext | undefined>(undefined)

export const WishlistProvider = ({ children }: WishlistProviderProps) => {
  const [isActive, setIsActive] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [wishlistId, setWishlistId] = useState<string | undefined>(undefined)
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([])
  const isLoadingRef = useRef(false)
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    // Initialize SwymCallbacks array if it doesn't exist
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = []
    }

    // Register our callback to be executed when Swym loads
    const onSwymLoadCallback = (swat: any) => {
      // Swym has loaded, now we can safely use the swat object
      if (swat && $settings.wishlistEnabled) {
        setIsActive(true)
        // Store swat globally for other components to use
        window._swat = swat
      } else {
        setIsActive(false)
      }
    }

    // Add our callback to the SwymCallbacks array
    window.SwymCallbacks.push(onSwymLoadCallback)

    // Cleanup function to remove our callback if component unmounts
    return () => {
      if (window.SwymCallbacks) {
        const index = window.SwymCallbacks.indexOf(onSwymLoadCallback)

        if (index > -1) {
          window.SwymCallbacks.splice(index, 1)
        }
      }
    }
  }, [])

  const fetchWishlistItems = useCallback(() => {
    if (!isActive || !wishlistId) {
      return Promise.resolve([])
    }

    // Don't fetch if already loading
    if (isLoadingRef.current) {
      return Promise.resolve([])
    }

    isLoadingRef.current = true

    return new Promise<WishlistItem[]>((resolve, reject) => {
      window._swat?.fetchListCtx(
        { lid: wishlistId },
        (response: WishlistItem[]) => {
          setWishlistItems(response)
          isLoadingRef.current = false
          resolve(response)
        },
        (error: unknown) => {
          console.error('Error fetching wishlist content', error)
          isLoadingRef.current = false
          reject(error as Error)
        },
      )
    })
  }, [isActive, wishlistId])

  // Fetch wishlist items when wishlistId becomes available
  useEffect(() => {
    if (wishlistId && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      fetchWishlistItems().catch(console.error)
    }
  }, [wishlistId, fetchWishlistItems])

  useEffect(() => {
    if (!isActive || !window._swat) {
      return
    }

    const initializeWishlist = (): Promise<string> => {
      return new Promise((resolve, reject) => {
        window._swat?.fetchLists({
          callbackFn: (lists: Wishlist[]) => {
            const defaultList = lists.find(
              (list) => list.lname === WISHLIST_NAME,
            )

            if (defaultList) {
              resolve(defaultList.lid)

              return
            }

            // Create default wishlist if it doesn't exist
            console.warn(
              `Wishlist "${WISHLIST_NAME}" not found. Creating new list.`,
            )
            window._swat?.createList(
              { lname: WISHLIST_NAME },
              (newList: Wishlist) => {
                console.info('Successfully created new list', newList.lid)
                resolve(newList.lid)
              },
              (error: unknown) => {
                console.error('Error creating wishlist', error)
                reject(error as Error)
              },
            )
          },
          errorFn: (error: unknown) => {
            console.error('Error fetching wishlist lists', error)
            reject(error as Error)
          },
        })
      })
    }

    initializeWishlist()
      .then(setWishlistId)
      .catch((error: Error) => {
        console.error('Error initializing wishlist', error)
      })
  }, [isActive])

  const addProduct = useCallback(
    (productData: WishlistProductData) => {
      return new Promise<void>((resolve, reject) => {
        if (!isActive) {
          reject(new Error('Wishlist is not active'))

          return
        }

        if (!wishlistId) {
          reject(new Error('Wishlist is not initialized yet.'))

          return
        }

        const { handle, variantId, productId, variantSelectedByUser } =
          productData

        const swymPayload = {
          epi: getIdFromShopifyGid(variantId),
          empi: getIdFromShopifyGid(productId),
          du: `${window.location.origin}/products/${handle}`,
          cprops: {
            variantSelectedByUser: Boolean(variantSelectedByUser),
          },
        } as const

        window._swat?.addToList(
          wishlistId,
          swymPayload,
          () => {
            console.info('Product has been added to wishlist!')

            // Refresh the wishlist cache after adding a product
            fetchWishlistItems()
              .then(() => {
                resolve()
                setIsDrawerOpen(true) // Open the drawer after adding
              })
              .catch(reject)
          },
          (error: unknown) => {
            console.error('Error adding product to wishlist', error)
            reject(error as Error)
          },
        )
      })
    },
    [isActive, wishlistId, fetchWishlistItems],
  )

  const removeProduct = useCallback(
    (productData: WishlistProductData) => {
      return new Promise<void>((resolve, reject) => {
        if (!isActive) {
          reject(new Error('Wishlist is not active'))

          return
        }

        if (!wishlistId) {
          reject(new Error('Wishlist is not initialized yet.'))

          return
        }

        const { handle, variantId, productId } = productData

        const numericVariantId = getIdFromShopifyGid(variantId)
        const numericProductId = getIdFromShopifyGid(productId)

        window._swat?.deleteFromList(
          wishlistId,
          {
            epi: numericVariantId,
            empi: numericProductId,
            du: `${window.location.origin}/products/${handle}`,
          },
          () => {
            console.info('Product has been removed from wishlist!')

            // Optimistically update local cache so UI (e.g., Wishlist CTA) reflects the change immediately
            setWishlistItems((prev) =>
              prev.filter(
                (item) =>
                  !(
                    item.empi === numericProductId &&
                    item.epi === numericVariantId
                  ),
              ),
            )

            // Optionally refresh in background to stay in sync with server (non-blocking)
            fetchWishlistItems().catch((error) => {
              console.error('Error refreshing wishlist', error)
            })

            resolve()
          },
          (error: unknown) => {
            console.error('Error removing product from wishlist', error)
            reject(error as Error)
          },
        )
      })
    },
    [isActive, wishlistId, fetchWishlistItems],
  )

  const getWishlistContent = useCallback(() => {
    // Return cached data if available, otherwise fetch fresh data
    if (!isEmpty(wishlistItems)) {
      return Promise.resolve(wishlistItems)
    }

    return fetchWishlistItems()
  }, [wishlistItems, fetchWishlistItems])

  // Synchronous function to check if product is in wishlist using cached data
  const isProductInWishlist = useCallback(
    (productId: string): boolean => {
      if (!isActive || !wishlistId) {
        return false
      }

      const numericId = getIdFromShopifyGid(productId)

      return wishlistItems.some((item) => {
        // Check both empi (product ID) and epi (variant ID) fields
        return item.empi === numericId || item.epi === numericId
      })
    },
    [isActive, wishlistId, wishlistItems],
  )

  return (
    <WishlistContext.Provider
      value={{
        isActive,
        isDrawerOpen,
        itemsCount: wishlistItems.length,
        wishlistItems,
        setIsDrawerOpen,
        addProduct,
        removeProduct,
        getWishlistContent,
        isProductInWishlist,
        refreshWishlist: fetchWishlistItems,
      }}
    >
      {children}
    </WishlistContext.Provider>
  )
}

export const useWishlist = () => {
  const context = useContext(WishlistContext)

  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }

  return context
}
